/**
 * Film Row Component
 * Netflix-style horizontal scrolling carousel
 * Supports row types: upnext (with now playing), toprated (with day/time), night (with colour tint)
 */

import { useState, useRef, useEffect, useCallback } from '@wordpress/element';
import FilmCard from './FilmCard';

export default function FilmRow({ title, films, onTrailerClick, link, accentColor, rowType }) {
    const scrollRef = useRef(null);
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [showRightArrow, setShowRightArrow] = useState(false);

    const checkArrows = useCallback(() => {
        const container = scrollRef.current;
        if (!container) return;

        const { scrollLeft, scrollWidth, clientWidth } = container;
        setShowLeftArrow(scrollLeft > 10);
        setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    }, []);

    useEffect(() => {
        checkArrows();
        const container = scrollRef.current;
        if (container) {
            container.addEventListener('scroll', checkArrows);
            window.addEventListener('resize', checkArrows);
        }
        return () => {
            if (container) {
                container.removeEventListener('scroll', checkArrows);
            }
            window.removeEventListener('resize', checkArrows);
        };
    }, [checkArrows, films]);

    const scroll = (direction) => {
        const container = scrollRef.current;
        if (!container) return;

        const scrollAmount = container.clientWidth * 0.8;
        container.scrollBy({
            left: direction === 'left' ? -scrollAmount : scrollAmount,
            behavior: 'smooth',
        });
    };

    if (!films || films.length === 0) return null;

    // Build class names and styles
    const isNight = rowType === 'night';
    const classNames = ['rialto-row'];
    if (isNight && accentColor) classNames.push('rialto-row--night');
    const rowStyle = {};
    if (isNight && accentColor) {
        rowStyle['--row-accent'] = accentColor;
    }

    return (
        <section className={classNames.join(' ')} style={rowStyle}>
            <div className="rialto-row-header">
                <h2 className="rialto-row-title">
                    {link ? (
                        <a href={link}>
                            {title}
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="9 18 15 12 9 6" />
                            </svg>
                        </a>
                    ) : (
                        title
                    )}
                </h2>
                {rowType === 'upnext' && (
                    <a href="/schedule/" className="rialto-row-see-all">
                        View Schedule
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="9 18 15 12 9 6" />
                        </svg>
                    </a>
                )}
            </div>

            <div className="rialto-row-container">
                {/* Left Arrow */}
                {showLeftArrow && (
                    <button
                        className="rialto-row-arrow rialto-row-arrow-left"
                        onClick={() => scroll('left')}
                        aria-label="Scroll left"
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="15 18 9 12 15 6" />
                        </svg>
                    </button>
                )}

                {/* Scrollable Content */}
                <div className="rialto-row-scroll" ref={scrollRef}>
                    <div className="rialto-row-films">
                        {films.map((film, idx) => (
                            <div
                                key={film.id || idx}
                                className={`rialto-row-item${film._isNowPlaying ? ' rialto-row-item--now-playing' : ''}`}
                            >
                                <FilmCard
                                    film={film}
                                    onTrailerClick={onTrailerClick}
                                    isNowPlaying={film._isNowPlaying}
                                />

                                {/* Time/date label under poster */}
                                {rowType === 'upnext' && film._screeningTime && (
                                    <div className={`rialto-row-time${film._isNowPlaying ? ' rialto-row-time--active' : ''}`}>
                                        {film._screeningTime}
                                    </div>
                                )}
                                {rowType === 'toprated' && film._screeningDay && film._screeningTime && (
                                    <div className="rialto-row-time">
                                        {film._screeningDay} {film._screeningTime}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Right Arrow */}
                {showRightArrow && (
                    <button
                        className="rialto-row-arrow rialto-row-arrow-right"
                        onClick={() => scroll('right')}
                        aria-label="Scroll right"
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="9 18 15 12 9 6" />
                        </svg>
                    </button>
                )}
            </div>
        </section>
    );
}
